import { useRef } from "react";
import { t } from "@/i18n-js/instance";
import { Button } from "@circle-react-uikit/Button";
import { ButtonWithDisabled } from "@circle-react-uikit/ButtonWithDisabled";
import { Form } from "@circle-react-uikit/Form";
import { Modal } from "@circle-react-uikit/ModalV2";
import { ScheduledEmailForm } from "./Form";
import { useScheduledEmailModalStore } from "./store";
import { useScheduledEmailsForm } from "./useScheduledEmailsForm";

export type ScheduledEmailType = "event" | "post";

export interface ScheduledEmail {
  email_body: Record<string, any>;
  enabled: boolean;
  id?: string;
  metadata: Record<string, string>;
  record_id: string;
  scheduled_at: Date;
  scheduled_email_type: ScheduledEmailType;
  subject: string;
}

interface ScheduledEmailModalProps {
  isDateDisabled?: boolean;
  isOpen: boolean;
  maxDate?: Date;
  minDate?: Date;
  onClose: () => void;
  scheduledEmail: ScheduledEmail;
}

export const ScheduledEmailModal = ({
  isDateDisabled = false,
  scheduledEmail,
  isOpen,
  onClose,
  maxDate,
  minDate,
}: ScheduledEmailModalProps) => {
  const initialFocusRef = useRef(null);
  const { isDirty, setIsDirty } = useScheduledEmailModalStore();
  const { saveScheduledEmail, isLoading } = useScheduledEmailsForm();

  const handleSubmit = async (params: Record<string, any>) => {
    try {
      await saveScheduledEmail(params);
      setIsDirty(false);
      onClose();
    } catch (err) {
      console.error(err);
    }
  };

  const id = scheduledEmail.id || "";

  const FORM_ID = `edit-scheduled-email-${id}`;

  const onCloseWithConfirm = () => {
    /* eslint-disable-next-line no-alert --
       We use a very obtrusive confirm dialog to make sure the
       user is aware of the consequences of closing the modal
     */
    if (!isDirty || confirm(t("prevent_accidental_leave_message"))) {
      onClose();
    }
  };

  return (
    <Form
      id={FORM_ID}
      onSubmit={handleSubmit}
      defaultValues={scheduledEmail}
      shouldStopPropagation
    >
      <Modal
        isOpen={isOpen}
        onClose={onCloseWithConfirm}
        contentPortalId="scheduled-email-type-picker-portal"
        initialFocus={initialFocusRef}
        className="!h-full"
      >
        <Modal.Overlay />
        <Modal.Content size="lg">
          <Modal.Header>
            <Modal.Title size="md">
              {t("scheduled_emails.form.title")}
            </Modal.Title>
            <Modal.HeaderActions>
              <Modal.CloseButton onClick={onCloseWithConfirm} />
            </Modal.HeaderActions>
          </Modal.Header>
          <Modal.Body>
            <ScheduledEmailForm
              isDateDisabled={isDateDisabled}
              maxDate={maxDate}
              minDate={minDate}
            />
          </Modal.Body>
          <Modal.Footer className="flex justify-end">
            {!isLoading && (
              <Button
                type="button"
                variant="secondary"
                className="mr-2"
                onClick={onCloseWithConfirm}
              >
                {t("cancel")}
              </Button>
            )}
            <ButtonWithDisabled
              type="submit"
              variant="circle"
              form={FORM_ID}
              isDisabled={isLoading}
            >
              {!isLoading && t("save")}
              {isLoading && t("saving")}
            </ButtonWithDisabled>
          </Modal.Footer>
        </Modal.Content>
      </Modal>
    </Form>
  );
};
